import { FC, /* useId, */ useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import {
  Button,
  // Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  Stack,
  Upload,
  useToaster,
} from '@forto-dev/phoenix-ui-core/common'

import { useAuth } from '@/features/auth'
import { customerDocumentsQueryOptions } from '@/features/company/api/useCompanyDocuments'
import { Features } from '@/utils/featureFlag/features'
import { useFeatureFlag } from '@/utils/featureFlag/useFeatureFlag'
import { useLocalisation } from '@/utils/localisation'
import { specialCharacterRegex } from '@/utils/validation'

import { CreateDocumentFormSchema, CreateDocumentFormSchemaType } from './schema'
import { documentTemplatesQueryOptions } from '../api/documents.service'
import { useDocumentCreate } from '../api/useDocumentCreate'
import { useDocumentUpdate } from '../api/useDocumentUpdate'

export const DocumentUpload: FC = () => {
  const { translate } = useLocalisation()
  const { user, initialData } = useAuth()
  const toaster = useToaster()
  const queryClient = useQueryClient()
  // const id = useId()
  const [uploadFormKey, setUploadFormKey] = useState(Math.random())
  const { data: documentTemplates } = useSuspenseQuery(documentTemplatesQueryOptions())
  const createDocumentMutation = useDocumentCreate()
  const uploadDocumentMutation = useDocumentUpdate()
  const [uploadFile, setUploadFile] = useState<File | undefined>(undefined)
  const [isUploading, setIsUploading] = useState(false)
  const { data: documents } = useSuspenseQuery(customerDocumentsQueryOptions(initialData.data!.customer._id))
  const { isFeatureEnabled } = useFeatureFlag()
  const isNewRequiredActionsModels = isFeatureEnabled(Features.NEW_REQUIRED_ACTIONS_MODELS)
  const {
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CreateDocumentFormSchema),
    defaultValues: {
      role: '',
      replace: false,
    },
  })
  const roles = useMemo(() => {
    return (documentTemplates ?? []).reduce(
      (acc, template) => {
        acc[template.documentTypeCode] = template.displayName
        return acc
      },
      {} as Record<string, string>
    )
  }, [documentTemplates])
  const documentWithSameRole = (documents ?? []).find(
    (document: { documentTypeCode: string }) => document.documentTypeCode === getValues('role')
  )

  const onSubmit: SubmitHandler<CreateDocumentFormSchemaType> = async (data) => {
    if (user && uploadFile && !isUploading) {
      setIsUploading(true)
      try {
        if (data.replace && documentWithSameRole) {
          await uploadDocumentMutation.mutateAsync({
            documentId: documentWithSameRole.id,
            customerId: user.customer_id ?? '?',
            data: {
              file: uploadFile,
              state: 'published',
              mimeType: uploadFile.type,
              customerID: user.customer_id,
              source: 'customer',
              documentTypeCode: data.role,
            },
          })

          await queryClient.invalidateQueries({
            queryKey: ['customerDocuments', initialData.data!.customer._id],
          })

          toaster({
            title: translate('company-documents.upload.notifications.updated'),
          })
        } else {
          await createDocumentMutation.mutateAsync({
            customerId: user.customer_id ?? '?',
            data: {
              file: uploadFile,
              state: 'published',
              mimeType: uploadFile.type,
              customerID: user.customer_id,
              source: 'customer',
              documentTypeCode: data.role,
            },
            isNewRequiredActionsModels,
          })

          await queryClient.invalidateQueries({
            queryKey: ['customerDocuments', initialData.data!.customer._id],
          })

          toaster({
            title: translate('company-documents.upload.notifications.uploaded'),
          })
        }
      } finally {
        setIsUploading(false)
        setUploadFile(undefined)
      }
    }
  }

  return (
    <>
      <Upload
        key={uploadFormKey}
        label={translate('company-documents.upload.label')}
        description={translate('company-documents.upload.description')}
        accept="image/jpeg,image/png,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={(files) => {
          const selectedFile = files?.[0]
          if (selectedFile) {
            // Check for special characters in the file name
            if (specialCharacterRegex.test(selectedFile.name)) {
              toaster({
                title: translate('documents.upload.error.label'),
                description: translate('documents.upload.error.description'),
                variant: 'negative',
              })
              setUploadFile(undefined)
              setUploadFormKey(Math.random())
              return
            }
          }
          setUploadFile(selectedFile ?? undefined)
          // Enforce re-render of the input field to clear any existing selection
          setUploadFormKey(Math.random())
        }}
      />

      <Dialog
        open={Boolean(uploadFile)}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setUploadFile(undefined)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate('company-documents.upload.dialog.title', {
                name: uploadFile?.name,
              })}
            </DialogTitle>
          </DialogHeader>
          <Stack gap={16}>
            <Select
              label={translate('company-documents.upload.dialog.fields.role.label')}
              value={getValues('role')}
              onChange={(newRole) => {
                setValue('role', newRole, { shouldValidate: true })
              }}
              state={errors.role ? 'negative' : undefined}
              items={Object.entries(roles).map(([role, label]) => ({
                value: role,
                label,
              }))}
              searchable
            />

            {/*
            // This checkbox when ticked is supposed to replace the existing document
            // But the current implementation adds a new file with the same upload date of the previous one
            // Until established that it's needed (Old SHIP didn't have it) and the behavior fixed, it should not show
            {documentWithSameRole ? (
              <Checkbox
                id={`${id}-replace`}
                label={translate('company-documents.upload.dialog.fields.replace.label')}
                checked={getValues('replace')}
                onCheckedChange={(checked) => {
                  setValue('replace', Boolean(checked), { shouldValidate: true })
                }}
              />
            ) : null}
             */}
          </Stack>
          <DialogFooter>
            <Button onClick={handleSubmit(onSubmit)} disabled={isUploading}>
              {translate('company-documents.upload.dialog.actions.upload')}
            </Button>
            <Button variant="outline-primary" onClick={() => setUploadFile(undefined)}>
              {translate('company-documents.upload.dialog.actions.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
